import { ChevronRight, Coins, Layers3, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { formatDate, formatToken } from '../lib/format';
import { atomicToDecimal } from '../state/AppDataContext';

/*
 * Generation income map (owner, 2026-09-17): the ten sponsor generations of one ID drawn as neon bands - generation 1
 * (direct sponsor income) on top, generations 2-10 (level income) below - each band listing its IDs as chips.
 * Owner 2026-10-11 (on-chain only): every amount is the SponsorIncome the contract actually paid this ID for that ID's
 * entry (or sent to the Referral Treasury instead); nothing is multiplied out from a rate. Shared by Lookup, the admin
 * console and the member panel.
 */

export interface GenerationIncomeMember {
  bdId: string;
  sponsorBdId: string | null;
  joinedAt: string;
  directCount: number;
  status: string;
  /** Placed in the matrix on-chain (absent from older APIs). */
  placed?: boolean;
  /** SponsorIncome this ID's entry paid to the root ID (absent from older APIs). */
  paidAtomic?: string;
  /** Same level sent to the Referral Treasury instead. */
  missedAtomic?: string;
}

export interface GenerationIncomeBand {
  depth: number;
  count: number;
  /** The contract's configured amount for this level (reference only); null when the chain has not reported it. */
  perIdAtomic: string | null;
  /** Received from this generation on-chain. */
  totalAtomic: string;
  paidCount?: number;
  missedAtomic?: string;
  missedCount?: number;
  members: GenerationIncomeMember[];
  truncated: boolean;
}

export interface GenerationIncomeView {
  bdId: string;
  ratePerDirectAtomic: string;
  ratePerLevelAtomic: string;
  ratesByDepthAtomic: string[];
  levelMaxDepth: number;
  generations: GenerationIncomeBand[];
  directCount: number;
  levelCount: number;
  directTotalAtomic: string;
  levelTotalAtomic: string;
  /** Name kept: equals receivedTotalAtomic (on-chain). */
  projectedTotalAtomic: string;
  receivedTotalAtomic?: string;
  missedTotalAtomic?: string;
  source?: 'onchain';
}

const btc = (atomic: string | null | undefined): string => formatToken(atomicToDecimal(atomic ?? '0'));
const ordinal = (depth: number): string => `${depth}${depth === 1 ? 'st' : depth === 2 ? 'nd' : depth === 3 ? 'rd' : 'th'}`;

export function GenerationIncomeMap({
  bdId,
  onSelectId,
  eyebrow = 'SPONSOR INCOME BY GENERATION',
  title = 'Generation income map',
  compact = false,
}: {
  bdId: string;
  onSelectId?: ((bdId: string) => void) | undefined;
  eyebrow?: string;
  title?: string;
  compact?: boolean;
}) {
  const [view, setView] = useState<GenerationIncomeView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openDepth, setOpenDepth] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setView(null);
    setError(null);
    setOpenDepth(null);
    api<GenerationIncomeView>(`/api/public/network/${encodeURIComponent(bdId)}/generations?limit=150`)
      .then((result) => { if (!cancelled) setView(result); })
      .catch((cause) => { if (!cancelled) setError(cause instanceof Error ? cause.message : 'Generation income could not be loaded.'); });
    return () => { cancelled = true; };
  }, [bdId]);

  const received = view ? view.receivedTotalAtomic ?? view.projectedTotalAtomic : '0';

  return (
    <section className={`panel generation-income${compact ? ' compact' : ''}`}>
      <div className="panel-heading">
        <div><span className="eyebrow">{eyebrow}</span><h2><Layers3 size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />{title}</h2></div>
        {view ? <span className="generation-total" title="Sum of the SponsorIncome events paid to this ID"><Coins size={13} /> Received {btc(received)}</span> : null}
      </div>
      {error ? <p className="empty-copy">{error}</p> : null}
      {!view && !error ? <p className="empty-copy">Loading generations...</p> : null}
      {view ? (
        <>
          <div className="generation-summary">
            <div className="generation-summary-box gen-direct">
              <span>1st generation (direct)</span>
              <strong>{view.directCount.toLocaleString()} ID{view.directCount === 1 ? '' : 's'}</strong>
              <small>received {btc(view.directTotalAtomic)}</small>
            </div>
            <div className="generation-summary-box gen-level">
              <span>2nd to {view.levelMaxDepth}th generation</span>
              <strong>{view.levelCount.toLocaleString()} ID{view.levelCount === 1 ? '' : 's'}</strong>
              <small>received {btc(view.levelTotalAtomic)}</small>
            </div>
            <div className="generation-summary-box gen-total">
              <span>Total sponsor income</span>
              <strong>{btc(received)}</strong>
              <small>paid on-chain (SponsorIncome){view.missedTotalAtomic && view.missedTotalAtomic !== '0' ? ` · ${btc(view.missedTotalAtomic)} went to the Referral Treasury` : ''}</small>
            </div>
          </div>
          <div className="generation-bands">
            {view.generations.map((band) => {
              const open = openDepth === band.depth || (openDepth === null && band.depth === 1);
              const shown = open ? band.members : band.members.slice(0, compact ? 6 : 12);
              return (
                <div key={band.depth} className={`generation-band depth-${band.depth}${band.count === 0 ? ' empty' : ''}`}>
                  <button type="button" className="generation-band-head" onClick={() => setOpenDepth(open && openDepth === band.depth ? -1 : band.depth)} aria-expanded={open}>
                    <span className="generation-badge">G{band.depth}</span>
                    <span className="generation-band-title">{band.depth === 1 ? '1st generation · direct sponsor income' : `${ordinal(band.depth)} generation · level income`}</span>
                    <span className="generation-band-stats" title={band.perIdAtomic ? `Level amount configured on-chain (EconomicsConfigured): ${btc(band.perIdAtomic)} (Standard era; Half / Quarter pay 1/2 / 1/4) - reference only, the amount shown is what was paid` : 'Level amount configured on-chain: — (not reported by the chain yet); the amount shown is what was paid'}>
                      <Users size={12} /> {band.count.toLocaleString()}{band.paidCount !== undefined ? ` · ${band.paidCount} paid` : ''} · <strong>{btc(band.totalAtomic)}</strong>
                      {band.missedCount ? ` · ${band.missedCount} to Treasury` : ''}
                    </span>
                    <ChevronRight size={14} className={open ? 'rotated' : ''} />
                  </button>
                  {band.count === 0 ? <p className="generation-empty">No ID in this generation yet.</p> : null}
                  {band.count > 0 ? (
                    <div className="generation-chips">
                      {shown.map((member) => {
                        const paid = member.paidAtomic !== undefined && member.paidAtomic !== '0';
                        const missed = member.missedAtomic !== undefined && member.missedAtomic !== '0';
                        const outcome = paid ? `paid you ${btc(member.paidAtomic)}` : missed ? `${btc(member.missedAtomic)} went to the Referral Treasury` : member.placed === false ? 'not placed on-chain - paid nothing' : 'no payment to you on-chain';
                        return (
                          <button
                            key={member.bdId}
                            type="button"
                            className={`generation-chip${member.status !== 'active' || member.placed === false ? ' inactive' : ''}`}
                            title={`${member.bdId} · sponsor ${member.sponsorBdId ?? 'root'} · joined ${formatDate(member.joinedAt)} · ${member.directCount} direct${member.directCount === 1 ? '' : 's'} · ${outcome}`}
                            onClick={() => onSelectId?.(member.bdId)}
                          >
                            <strong>{member.bdId}</strong>
                            <small>{paid ? btc(member.paidAtomic) : band.depth === 1 ? `${member.directCount} direct${member.directCount === 1 ? '' : 's'}` : `via ${member.sponsorBdId ?? '?'}`}</small>
                          </button>
                        );
                      })}
                      {!open && band.members.length > shown.length ? <button type="button" className="generation-chip more" onClick={() => setOpenDepth(band.depth)}>+{(band.count - shown.length).toLocaleString()} more</button> : null}
                      {open && band.truncated ? <span className="generation-chip more">first {band.members.length} of {band.count.toLocaleString()}</span> : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </>
      ) : null}
    </section>
  );
}
